<template>
  <!--
    Product cards render their OWN responsive <picture> via this atom imported
    DIRECTLY (ResponsiveImage = src/core/.../atoms/image/image.vue). We do NOT use
    the global <vf-a-image>, because the build maps that tag to
    checkout-app/lib/.../image.vue (the old background-image atom that ignores
    srcset). Importing the real atom here keeps the change scoped to product
    cards only — cart/checkout (which still use vf-a-image) are untouched.
  -->
  <ResponsiveImage
    :src="mainImage"
    :srcset="avifSrcset"
    :webp-src="webpSrc"
    :img-srcset="imgSrcset"
    :sizes="sizesAttr"
    :alt="productImageAlt"
    :title="productImageAlt"
    :width-amp="width ? width : $vuefront.images.productThumb.width"
    :height-amp="height ? height : $vuefront.images.productThumb.height"
    :width="width ? width : null"
    :class="{ 'card-img': card }"
    :fluid="!width && !height"
    :eager="priority"
    class="vf-m-new-product-thumb-image"
    @load="emit('image:settled')"
    @error="handleImageError"
  />
</template>
<script setup lang="ts">
import {
  inject,
  onMounted,
  PropType,
  computed,
  ref,
  toValue,
  watch,
} from "vue";
import {
  cardPhotoSizes,
  PRODUCT_CARD_IMAGE_SIZES,
  PRODUCT_CARD_IMAGE_SIZES_KEY,
  productCardImageRequest,
  productCardMediaRequest,
  productCardPhotoKey,
  rememberCatalogCardPhoto,
} from "~/core/utils/catalogImageWarmup";
import { buildProductCardImageAlt } from "~/core/utils/productImageAlt";
// Import the real responsive <picture> atom directly (NOT the global vf-a-image,
// which resolves to the old checkout-app background-image atom). Scoped to cards.
// The structural .vf-a-image* classes are styled by the global (checkout-app)
// image.scss already loaded site-wide; the card image's fit/sizing is pinned
// deterministically in this component's <style> below.
import ResponsiveImage from "~/core/components/atoms/image/image.vue";

const vuefront$ = inject<any>("$vuefront");
const emit = defineEmits(["image:settled"]);
const props = defineProps({
  card: {
    type: Boolean,
    default() {
      return false;
    },
  },
  product: {
    type: Object,
    default() {
      return null;
    },
  },
  width: {
    type: [String, Number] as PropType<string | number>,
    default() {
      return null;
    },
  },
  height: {
    type: [String, Number] as PropType<string | number>,
    default() {
      return null;
    },
  },
  layout: {
    type: String,
    default() {
      return null;
    },
  },
  priority: {
    type: Boolean,
    default() {
      return false;
    },
  },
});

const imageFailed = ref(false);
// Reuse one deterministic SEO alt across every rendition of this card image.
const productImageAlt = computed(() => buildProductCardImageAlt(props.product));

// The `sizes` of the listing this card sits in: the catalog grid provides
// its own, which follows its phone mode (organisms/catalog-product); every
// other card keeps the card box of PRODUCT_CARD_IMAGE_SIZES.
const listingSizes = inject(
  PRODUCT_CARD_IMAGE_SIZES_KEY,
  PRODUCT_CARD_IMAGE_SIZES
);
// The `sizes` this card's photo is requested with, fixed when the card
// mounts. A card already on the page keeps them when the listing's sizes
// change (the catalog's phone mode): the server HTML cards are requested
// while the page parses, and the grid mounts other cards only near the
// viewport, inside the browsers' lazy-load distance, so a new value would
// download the same photo a second time. Cards that mount later take the
// new sizes, unless their photo was already requested in this tab: then
// they ask with the sizes it was requested with (cardPhotoSizes) and find
// it in the cache (owner, 2026-10-16: no photo downloads twice; the photos
// already loaded stay as they are, the next ones load at the new size).
const photoKey = computed(() => productCardPhotoKey(props.product));
const sizesAttr = ref(cardPhotoSizes(photoKey.value, toValue(listingSizes)));
onMounted(() => rememberCatalogCardPhoto(photoKey.value, sizesAttr.value));
// Another photo in this card is a new request.
watch(photoKey, (key) => {
  sizesAttr.value = cardPhotoSizes(key, toValue(listingSizes));
  rememberCatalogCardPhoto(key, sizesAttr.value);
});

// The card's request (src, AVIF srcset, base WebP, sizes) comes from the one
// helper the catalog image warm-up also uses (utils/catalogImageWarmup), so a
// warmed image is exactly the one this card asks for.
// Cards render small (<=405px CSS; a phone's full width only in the catalog's
// one-column mode), so the 600 square AVIF is the <img> src at every
// breakpoint; the AVIF srcset (600w, 1200w) lets the browser take the 1200
// where the card's sizes ask for more than 600 device px (in the catalog
// grid: 3x phones, wider tablets, DPR2 from 1600px). It is the src
// when the product has AVIF renditions, so the card stops over-fetching the
// full-resolution base image even where vf-a-image renders a background-image
// rather than an <img>. Products with no AVIF master keep their raster image.
// That legacy request is the only one of a photo without `media`, and the
// fallback of a `media` photo that fails to load.
const cardImage = computed(() =>
  productCardImageRequest(props.product?.image, sizesAttr.value)
);

// The photo's own `media` (phase 6, utils/productMedia): the AVIF ladder for
// the card box and the JPEG <img>. molecules/new-product-thumb gives each
// gallery slide only its own `media`, never the main photo's.
const mediaImage = computed(() =>
  productCardMediaRequest(props.product?.media, sizesAttr.value)
);
const mediaFailed = ref(false);
const showsMedia = computed(() => !!mediaImage.value && !mediaFailed.value);

watch(
  () => props.product?.media,
  () => {
    mediaFailed.value = false;
  }
);

// On error, fall back to the product alt image or the global placeholder.
const mainImage = computed(() => {
  if (showsMedia.value) return mediaImage.value!.src;
  if (!cardImage.value) return vuefront$.images.placeholder.image;
  if (imageFailed.value)
    return props.product.imageAlt || vuefront$.images.placeholder.image;
  return cardImage.value.src;
});

// Responsive AVIF (+ base WebP) derived from the API image URL. Both are empty
// strings when the product has no renditions (a .png/.jpg master) or after an
// error, so vf-a-image renders a plain img with no broken sources / 404s.
const avifSrcset = computed(() => {
  if (showsMedia.value) return mediaImage.value!.avifSrcset;
  return imageFailed.value ? "" : cardImage.value?.avifSrcset || "";
});
const webpSrc = computed(() =>
  showsMedia.value || imageFailed.value ? "" : cardImage.value?.webpSrc || ""
);
const imgSrcset = computed(() =>
  showsMedia.value ? mediaImage.value!.imgSrcset || "" : ""
);

function handleImageError() {
  // A `media` photo that fails steps to the legacy photo (it settles there).
  if (showsMedia.value) {
    mediaFailed.value = true;
    return;
  }
  imageFailed.value = true;
  emit("image:settled");
}
</script>

<style>
/*
  The card image is now a real <img> inside the imported <picture> atom (it used
  to be a CSS background-image div). Pin it to fill the card box and show the
  WHOLE product un-cropped — object-fit: contain, matching the previous
  background-size: contain look (NOT cover, which zoomed/cropped it). This
  selector (.vf-m-new-product-thumb-image .vf-a-image__img) is more specific than
  the atom's .vf-a-image__img / --cover rules, so it deterministically wins
  regardless of which image.scss loads last. Scoped to product cards only.
*/
.vf-m-new-product-thumb-image .vf-a-image__img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: transparent;
}
</style>
